import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Bandeau } from './Champs'
import { ErreurApi } from '../api'
import { EcranErreur } from './Erreurs'
import {
  archiverFil,
  envoyerMessage,
  lireFil,
  listerFils,
  listerMessages,
  marquerFilLu,
  type Fil,
  type Message,
} from '../apiRelations'
import { TEXTES, messageErreur, type Langue } from '../i18n'
import { EtatVide, Icone, Image, LienBouton, SqueletteListe } from '../ui'

/**
 * Messagerie — L4, refondue à la recette du 08/09 (ticket `fo-messages-01`).
 *
 * **Deux panneaux sur grand écran, un seul sur mobile.** L'ancienne écriture faisait deux PAGES :
 * une liste, puis un fil, sans retour visuel de l'un à l'autre. Passer d'une conversation à la
 * suivante demandait donc un aller-retour complet, et on perdait de vue à qui l'on écrivait. La
 * maquette met la liste à gauche et le fil à droite, ce qui est l'anatomie que toute messagerie
 * partage — et ce n'est pas une mode : c'est ce qui permet de comparer deux fils sans les fermer.
 *
 * **Un seul composant pour les deux routes.** `/messages` et `/messages/:id` rendent la même
 * chose ; ce qui change est la présence d'un fil sélectionné. Sur mobile, l'attribut `data-vue`
 * décide lequel des deux panneaux occupe l'écran. Deux composants séparés — c'était l'écriture
 * précédente — donnent deux comportements qui divergent au premier correctif.
 *
 * **Le masquage s'explique, il ne se subit pas.** Un numéro remplacé par un caractère de
 * remplacement, sans un mot, se lit comme une panne : l'écran dit donc *pourquoi* c'est masqué et
 * *quand* ça ne le sera plus. C'est la seule chose qui distingue une règle d'un bug.
 *
 * **Aucun masquage n'est fait ici.** Le corps arrive déjà projeté du serveur ; une seconde
 * implémentation côté client divergerait de la première en silence, et le jour où elle divergerait
 * ce serait dans le sens qui fuit.
 *
 * **Ni WebSocket ni long polling** (D9) : on relit à l'ouverture et après un envoi. Le temps réel
 * arrivera si l'usage le réclame, pas parce que c'est plus joli.
 */

/** Les trois onglets de la maquette. */
type Filtre = 'tous' | 'nonLus' | 'archives'

export function Messagerie({
  langue,
  moi,
  id,
}: {
  langue: Langue
  moi: string | null
  id?: string
}) {
  const t = TEXTES[langue]
  const [filtre, setFiltre] = useState<Filtre>('tous')
  const [fils, setFils] = useState<Fil[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  const chargerFils = useCallback(async () => {
    try {
      // « Archivés » est une AUTRE liste, pas un filtre sur la première : le serveur ne les mélange
      // jamais. Archiver veut dire « hors de ma vue », et les faire réapparaître au milieu des
      // autres annulerait le geste.
      const page = await listerFils(1, 20, filtre === 'archives')

      setFils(filtre === 'nonLus' ? page.items.filter((fil) => fil.unreadCount > 0) : page.items)
      setErreur(null)
    } catch (cause: unknown) {
      setFils([])
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    }
  }, [filtre, langue])

  useEffect(() => {
    void chargerFils()
  }, [chargerFils])

  // `?annonce=` : « Candidatures » ouvre la conversation d'une annonce sans connaître son fil.
  // Le premier fil de cette annonce est le bon — un candidat n'en a qu'un par annonce.
  const [parametres] = useSearchParams()
  const navigate = useNavigate()
  const annonceDemandee = parametres.get('annonce')

  useEffect(() => {
    if (annonceDemandee === null || id !== undefined || fils === null) {
      return
    }

    const fil = fils.find((candidat) => candidat.listingId === annonceDemandee)

    if (fil !== undefined) {
      navigate(`/messages/${fil.id}`, { replace: true })
    }
  }, [annonceDemandee, fils, id, navigate])

  return (
    <section className="messagerie-ecran">
      <div className="page-entete">
        <div className="page-entete__rangee">
          <h2>{t.messages}</h2>

          <div className="rangee rangee--serree">
            {FILTRES.map((option) => (
              <button
                key={option}
                type="button"
                /* L'etat actif se dit par aria-pressed, que la feuille style deja : une classe
                   de plus dirait la meme chose une seconde fois, et les deux finiraient par ne
                   plus etre d'accord. */
                className="puce puce--action"
                aria-pressed={filtre === option}
                onClick={() => setFiltre(option)}
              >
                {t[LIBELLE_FILTRE[option]]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}

      {/*
        `data-vue` porte la mise en page compacte : sous 60 rem, un seul des deux panneaux est
        visible, et c'est la présence d'un fil sélectionné qui décide lequel. Au-delà, les deux
        cohabitent et l'attribut ne sert plus à rien — ce que dit la feuille de style, pas ce
        composant.
      */}
      <div className="messagerie" data-vue={id === undefined ? 'liste' : 'fil'}>
        <div className="fils">
          {fils === null && <SqueletteListe />}

          {fils !== null && fils.length === 0 && erreur === null && (
            <EtatVide
              icone="message"
              titre={filtre === 'tous' ? t.aucunFil : t.aucunFilFiltre}
              action={
                filtre === 'tous' ? (
                  <LienBouton to="/recherche" apparence="secondaire">
                    {t.rechercheChercher}
                  </LienBouton>
                ) : undefined
              }
            />
          )}

          {fils?.map((fil) => (
            <Link
              key={fil.id}
              to={`/messages/${fil.id}`}
              className={fil.id === id ? 'fil-entree est-actif' : 'fil-entree'}
            >
              <span className="avatar">{initiales(fil.otherDisplayName)}</span>
              <span className="fil-entree__corps">
                <span className="fil-entree__nom">
                  {fil.otherDisplayName ?? '—'}
                  <span className="fil-entree__date">{dateCourte(langue, fil.lastMessageAt)}</span>
                </span>
                <span className="fil-entree__apercu">{fil.lastMessagePreview ?? ''}</span>
                <span className="fil-entree__objet">{fil.listingTitle ?? ''}</span>
              </span>
              {fil.unreadCount > 0 && (
                <span className="compteur" aria-label={`${fil.unreadCount} ${t.messageNonLus}`}>
                  {new Intl.NumberFormat(langue).format(fil.unreadCount)}
                </span>
              )}
            </Link>
          ))}
        </div>

        {id === undefined ? (
          /* Panneau vide : il n'apparaît qu'au large, où la place existe. Un panneau blanc sans
             un mot se lit comme un défaut de chargement. */
          <div className="fil fil--vide">
            <p className="socle">{t.filChoisir}</p>
          </div>
        ) : (
          <FilDeDiscussion key={id} langue={langue} moi={moi} id={id} onArchive={chargerFils} />
        )}
      </div>
    </section>
  )
}

const FILTRES: readonly Filtre[] = ['tous', 'nonLus', 'archives']

const LIBELLE_FILTRE: Record<Filtre, 'filtreTous' | 'filtreNonLus' | 'filtreArchives'> = {
  tous: 'filtreTous',
  nonLus: 'filtreNonLus',
  archives: 'filtreArchives',
}

/**
 * Les initiales d'un nom, pour l'avatar.
 *
 * Deux lettres au plus : trois ne tiennent pas dans un disque de 40 px, et la troisième est
 * rarement celle qui distingue. Un nom absent rend un tiret plutôt qu'un disque vide, qui se
 * lirait comme une image cassée.
 */
function initiales(nom: string | null): string {
  if (nom === null || nom.trim() === '') {
    return '—'
  }

  return nom
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((mot) => mot[0]?.toUpperCase() ?? '')
    .join('')
}

/** Un fil, ses messages, et l'état du masquage annoncé en haut. */
export function FilDeDiscussion({
  langue,
  moi,
  id,
  onArchive,
}: {
  langue: Langue
  moi: string | null
  id: string
  onArchive?: () => void
}) {
  const t = TEXTES[langue]
  const navigate = useNavigate()

  const [fil, setFil] = useState<Fil | null>(null)
  const [messages, setMessages] = useState<Message[] | null>(null)
  const [brouillon, setBrouillon] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  /** La cause de l'échec du chargement du fil — un écran entier, pas un bandeau. */
  const [echec, setEchec] = useState<unknown>(null)

  const bas = useRef<HTMLDivElement | null>(null)
  /** Le fil a été lu au moins une fois : un échec ultérieur est un bandeau, pas un écran. */
  const filLu = useRef(false)

  const charger = useCallback(async () => {
    setEchec(null)

    try {
      const [entete, page] = await Promise.all([lireFil(id), listerMessages(id)])

      setFil(entete)
      filLu.current = true

      // Le serveur rend les plus récents d'abord ; l'écran lit du plus ancien au plus récent.
      setMessages([...page.items].reverse())
      setErreur(null)
    } catch (cause: unknown) {
      // Un fil jamais lu — identifiant inconnu, fil d'un autre compte, API muette — est un écran ;
      // un rechargement raté d'un fil déjà affiché reste un bandeau au-dessus des messages.
      if (!filLu.current) {
        setEchec(cause ?? new Error())
        return
      }

      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    }
  }, [id, langue])

  useEffect(() => {
    void charger()
  }, [charger])

  // Marquer lu APRÈS avoir affiché, et sans bloquer : un compteur qui ne retombe pas est un défaut
  // bénin, une lecture qui échoue parce que le marquage a échoué ne l'est pas.
  useEffect(() => {
    if (messages !== null && messages.length > 0) {
      void marquerFilLu(id).catch(() => undefined)
    }
  }, [id, messages])

  useEffect(() => {
    bas.current?.scrollIntoView({ block: 'end' })
  }, [messages])

  const envoyer = useCallback(async () => {
    const corps = brouillon.trim()

    if (corps.length === 0 || envoi) {
      return
    }

    setEnvoi(true)

    try {
      await envoyerMessage(id, corps)
      setBrouillon('')
      await charger()
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    } finally {
      setEnvoi(false)
    }
  }, [brouillon, charger, envoi, id, langue])

  const archiver = useCallback(async () => {
    try {
      await archiverFil(id)
      onArchive?.()
      navigate('/messages')
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    }
  }, [id, langue, navigate, onArchive])

  // Revu le 16/09/2026 (`docs/ui/pwa/introuvable.html`) : un fil que l'API ne rend pas est un état
  // vide dans le panneau du fil — la liste reste à côté, au large, et c'est elle la sortie.
  if (echec !== null && fil === null) {
    return (
      <div className="fil fil--erreur">
        <EcranErreur cause={echec} objet="conversation" onReessayer={() => void charger()} />
      </div>
    )
  }

  return (
    <div className="fil">
      <div className="fil__entete">
        {/* Le retour n'existe qu'en compact, où la liste n'est pas à l'écran. Au large il
            pointerait vers un panneau déjà visible. */}
        <Link to="/messages" className="fil__retour" aria-label={t.retourFils}>
          <Icone nom="chevron" taille="sm" arriere />
        </Link>

        <span className="avatar">{initiales(fil?.otherDisplayName ?? null)}</span>

        <div className="fil__identite">
          <p className="fil__nom">{fil?.otherDisplayName ?? t.messages}</p>
          {fil?.listingTitle !== null && fil?.listingTitle !== undefined && (
            <p className="aide">{fil.listingTitle}</p>
          )}
        </div>

        <div className="fil__actions">
          {fil?.listingId !== null && fil?.listingId !== undefined && (
            <Link to={`/annonces/${fil.listingId}`} className="puce puce--action">
              {t.filVoirAnnonce}
            </Link>
          )}
          {fil !== null && fil.status !== 'Archived' && (
            <button type="button" className="puce puce--action" onClick={() => void archiver()}>
              {t.archiver}
            </button>
          )}
        </div>
      </div>

      {/*
        **De quelle annonce on parle** — ticket `fo-message-fil-01`, et c'est ce que montre la
        maquette.

        L'en-tête portait le titre de l'annonce en petit, sous le nom de l'interlocuteur. Or un fil
        de discussion s'ouvre souvent plusieurs jours après le premier message, parfois sur un
        téléphone, parfois entre deux autres fils : « Rachid B. » ne suffit pas à se rappeler
        laquelle des trois chambres visitées est en jeu. La photo, elle, y suffit d'un coup d'œil.

        Elle est un LIEN vers la fiche, et non une décoration : c'est le geste qu'on fait ensuite —
        relire le prix avant de répondre.

        Le bloc n'apparaît que sur un fil d'annonce. Une conversation de foyer ou de support n'en
        a pas, et lui en fabriquer un vide serait pire que de s'en passer.
      */}
      {fil?.listingId !== null && fil?.listingId !== undefined && (
        <Link to={`/annonces/${fil.listingId}`} className="fil__annonce">
          <Image
            mediaId={fil.listingCoverMediaId}
            alt=""
            ratio="4-3"
            sizes="6rem"
            className="fil__annonce-photo"
          />
          <span className="fil__annonce-corps">
            <span className="fil__annonce-titre">{fil.listingTitle}</span>
            <span className="aide">{t.filVoirAnnonce}</span>
          </span>
          <Icone nom="chevron" taille="sm" miroir />
        </Link>
      )}

      {/*
        L'état du masquage est annoncé AVANT les messages, jamais après : c'est ce qui transforme
        un caviardage inexpliqué en règle comprise.
      */}
      {fil !== null && (
        <div className="fil__avis">
          <Bandeau ton={fil.contactDetailsVisible ? 'succes' : 'info'}>
            {fil.contactDetailsVisible ? t.masquageLeve : t.masquageActif}
            {!fil.contactDetailsVisible && <> {t.masquagePourquoi}</>}
          </Bandeau>
        </div>
      )}

      {erreur !== null && (
        <div className="fil__avis">
          <Bandeau ton="erreur">{erreur}</Bandeau>
        </div>
      )}

      <div className="fil__messages">
        {messages === null && <SqueletteListe />}

        {messages?.map((message, rang) => (
          <MessageBulle
            key={message.id}
            message={message}
            langue={langue}
            /* Le séparateur de jour n'apparaît qu'au CHANGEMENT de jour, et jamais avant le
               premier message : « Mardi 2 septembre » posé au-dessus d'un fil d'un seul jour
               n'apprend rien. */
            jour={rang === 0 || !memeJour(messages[rang - 1].sentAt, message.sentAt)}
            /* `moi` peut être nul le temps que le profil arrive : les bulles s'affichent alors
               toutes du côté de l'interlocuteur, ce qui est faux mais lisible — et se corrige au
               rendu suivant. Bloquer l'écran sur le profil serait pire. */
            deMoi={moi !== null && message.senderUserId === moi}
          />
        ))}

        <div ref={bas} />
      </div>

      {fil?.status === 'Active' && (
        <form
          className="fil__saisie"
          onSubmit={(evenement) => {
            evenement.preventDefault()
            void envoyer()
          }}
        >
          <input
            type="text"
            value={brouillon}
            placeholder={t.ecrireMessage}
            aria-label={t.ecrireMessage}
            onChange={(evenement) => setBrouillon(evenement.target.value)}
          />
          <button
            type="submit"
            className="bouton"
            aria-label={t.envoyerMessage}
            disabled={envoi || brouillon.trim().length === 0}
          >
            <Icone nom="envoyer" miroir />
          </button>
        </form>
      )}
    </div>
  )
}

/**
 * Une bulle.
 *
 * **Le côté porte l'auteur.** Un fil à deux participants n'a pas besoin d'un nom au-dessus de
 * chaque message : la position suffit, et c'est ce que fait toute messagerie. Le nom reste dans
 * l'en-tête du fil, où il ne se répète pas quarante fois.
 *
 * `bulle--moi` est posée en propriété LOGIQUE (`margin-inline-start: auto`) : en arabe, mes
 * messages passent à gauche sans une ligne de plus (invariant 11).
 */
function MessageBulle({
  message,
  langue,
  jour,
  deMoi,
}: {
  message: Message
  langue: Langue
  jour: boolean
  deMoi: boolean
}) {
  const t = TEXTES[langue]

  return (
    <>
      {jour && <p className="fil__jour">{dateJour(langue, message.sentAt)}</p>}

      <div className={deMoi ? 'bulle bulle--moi' : 'bulle'}>
        <span>{message.body}</span>
        <span className="bulle__heure">
          <time dateTime={message.sentAt}>{heure(langue, message.sentAt)}</time>
          {message.wasMasked && <span className="bulle__masque">{t.messageMasque}</span>}
        </span>
      </div>
    </>
  )
}

/** Le fil ouvert depuis l'URL. */
export function FilRoute({ langue, moi }: { langue: Langue; moi: string | null }) {
  const { id } = useParams<{ id: string }>()

  return <Messagerie langue={langue} moi={moi} id={id} />
}

function memeJour(a: string, b: string): boolean {
  const gauche = new Date(a)
  const droite = new Date(b)

  return (
    gauche.getFullYear() === droite.getFullYear() &&
    gauche.getMonth() === droite.getMonth() &&
    gauche.getDate() === droite.getDate()
  )
}

/**
 * Dates rendues par `Intl`, jamais par une concaténation.
 *
 * C'est la seule façon d'obtenir un calendrier et un ordre corrects en arabe sans écrire deux
 * formateurs — et l'une des douze règles que L10 demande de tenir dès maintenant.
 */
export function dateCourte(langue: Langue, iso: string): string {
  return new Intl.DateTimeFormat(langue, { day: 'numeric', month: 'short' }).format(new Date(iso))
}

/** Le séparateur de jour : « mardi 2 septembre ». */
export function dateJour(langue: Langue, iso: string): string {
  return new Intl.DateTimeFormat(langue, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(iso))
}

export function heure(langue: Langue, iso: string): string {
  return new Intl.DateTimeFormat(langue, { hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  )
}

export function dateLongue(langue: Langue, iso: string): string {
  return new Intl.DateTimeFormat(langue, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}
